import { useEffect, useState } from 'react';
import { Modal, toast } from '@/components';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { buildApplyUrl } from '@/lib/apply-url';
import styles from './ViewingResultModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  viewingId: string | null;
  /** 登録成功時に親側で一覧を refetch するためのコールバック */
  onSaved?: () => void;
  /** タイトルに顧客名など追加情報を表示したい場合 */
  subTitle?: string;
}

interface ViewingPatchResponse {
  id: string;
  customer_id: string;
  status: string;
  result: string | null;
  application_form_token: string | null;
  customer_status_advanced: boolean;
}

const RESULT_OPTIONS: Array<{ value: 'interested' | 'applied' | 'rejected'; label: string; desc: string }> = [
  { value: 'interested', label: '興味あり', desc: '顧客を「内見済」に昇格し、再提案の候補に' },
  { value: 'applied', label: '申込', desc: '顧客を「申込中」に昇格し、申込フォーム URL を自動発行' },
  { value: 'rejected', label: '見送り', desc: '顧客を「内見済」に昇格し、別条件で再提案' },
];

export function ViewingResultModal({ open, onClose, viewingId, onSaved, subTitle }: Props) {
  const { user } = useAuthStore();
  const [result, setResult] = useState<'interested' | 'applied' | 'rejected'>('interested');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setResult('interested');
      setNote('');
      setIssuedToken(null);
    }
  }, [open, viewingId]);

  const handleSave = async () => {
    if (!viewingId) return;
    setSaving(true);
    try {
      const resp = await api<ViewingPatchResponse>(`/tenant/customer-viewings/${viewingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'done', result, note: note || undefined }),
      });
      const token = resp.application_form_token;
      const advanced = resp.customer_status_advanced;
      if (result === 'applied' && token) {
        setIssuedToken(token);
        toast(advanced ? '申込中に昇格・申込フォームを発行しました' : '申込フォームを発行しました', 'success');
      } else {
        toast(advanced ? '内見済に昇格しました' : '内見結果を登録しました', 'success');
      }
      onSaved?.();
      // token 発行時は URL 表示のためモーダルを開いたままにする
      if (!token) onClose();
    } catch (e) {
      toast((e as Error)?.message || '登録に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!issuedToken) return;
    try {
      await navigator.clipboard.writeText(buildApplyUrl(issuedToken, { slug: user?.tenantSlug }));
      toast('URL をコピーしました', 'success');
    } catch {
      toast('コピーに失敗しました', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`✅ 内見結果登録${subTitle ? ` - ${subTitle}` : ''}`}
      width={480}
      footer={
        <div className={styles.footer}>
          <button className={styles.btnOutline} onClick={onClose} disabled={saving}>
            {issuedToken ? '閉じる' : 'キャンセル'}
          </button>
          {!issuedToken && (
            <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? '登録中...' : '登録'}
            </button>
          )}
        </div>
      }
    >
      {issuedToken ? (
        <div className={styles.body}>
          <div className={styles.label}>申込フォーム URL</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              className={styles.textarea}
              readOnly
              value={buildApplyUrl(issuedToken, { slug: user?.tenantSlug })}
              style={{ flex: 1, padding: 8, fontSize: 12 }}
              onFocus={e => e.currentTarget.select()}
            />
            <button className={styles.btnPrimary} type="button" onClick={handleCopyUrl}>コピー</button>
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink3)' }}>
            顧客にこの URL を送付し、入居申込の入力を依頼してください。
          </div>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.label}>結果</div>
          <div className={styles.resultGrid}>
            {RESULT_OPTIONS.map(opt => (
              <label key={opt.value} className={`${styles.resultOption} ${result === opt.value ? styles.resultOptionActive : ''}`}>
                <input
                  type="radio"
                  name="viewing-result"
                  value={opt.value}
                  checked={result === opt.value}
                  onChange={() => setResult(opt.value)}
                />
                <div>
                  <div className={styles.resultName}>{opt.label}</div>
                  <div className={styles.resultDesc}>{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div className={styles.label} style={{ marginTop: 14 }}>メモ (任意)</div>
          <textarea
            className={styles.textarea}
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="顧客の反応・気になった点・次回アクションなど"
          />
        </div>
      )}
    </Modal>
  );
}
